import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

// ─── Types ────────────────────────────────────────────────────────────────────

export type AuthUser = {
    id: number
    name: string
    email: string
    role_id: number | null
    company_id: number | null
    use_pin_login?: boolean
    is_active?: boolean
    access_all_sucursals?: boolean
    avatar_url?: string | null
    last_login_at?: string | null
    role?: {
        id: number
        code: number
        name: string
    } | null
}

// ─── Session Store ────────────────────────────────────────────────────────────
// Backed by sessionStorage: el token NUNCA toca localStorage.
// sessionStorage sobrevive recargas (F5) dentro de la misma pestaña
// pero se limpia automáticamente al cerrar el browser/tab.

type SessionState = {
    token: string | null
    user: AuthUser | null

    setToken: (token: string | null) => void
    setUser: (user: AuthUser | null) => void
    logout: () => void
}

export const useSessionStore = create<SessionState>()(
    persist(
        (set) => ({
            token: null,
            user: null,

            setToken: (token) => set({ token }),
            setUser: (user) => set({ user }),

            logout: () => set({ token: null, user: null }),
        }),
        {
            name: 'session-storage',
            storage: createJSONStorage(() => sessionStorage),
        },
    ),
)

// ─── Device Store ─────────────────────────────────────────────────────────────
// Backed by localStorage: únicamente el UUID del dispositivo de confianza.
// Persiste entre sesiones y pestañas para que el dispositivo siga siendo reconocido.

export type TrustedDevice = {
    device_uuid: string
    device_name: string
    sucursal_id: number
}

type DeviceState = {
    trustedDevice: TrustedDevice | null

    setTrustedDevice: (device: TrustedDevice) => void
    clearTrustedDevice: () => void
}

export const useDeviceStore = create<DeviceState>()(
    persist(
        (set) => ({
            trustedDevice: null,

            setTrustedDevice: (device) => set({ trustedDevice: device }),
            clearTrustedDevice: () => set({ trustedDevice: null }),
        }),
        {
            name: 'device-storage', // localStorage['device-storage'] = solo el device_uuid
            storage: createJSONStorage(() => localStorage),
        },
    ),
)

// ─── Backwards-compat shim ────────────────────────────────────────────────────
// Permite usar useAuthStore() para código que todavía no se migró.
// Combina ambos stores en una vista unificada.

export const useAuthStore = () => {
    const session = useSessionStore()
    const device = useDeviceStore()

    return {
        // Session
        token: session.token,
        user: session.user,
        setToken: session.setToken,
        setUser: session.setUser,

        // Device
        trustedDevice: device.trustedDevice,
        setTrustedDevice: device.setTrustedDevice,
        clearTrustedDevice: device.clearTrustedDevice,

        // Logout unificado
        logout: () => {
            session.logout()
            // El trustedDevice se mantiene para volver al avatar picker
        },
    }
}